import { Component, Input, OnChanges, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ResizeEvent } from "angular-resizable-element";
import { ToastrService } from "ngx-toastr";
import { Determinant } from "../../../models/determinant";
import { DeterminantRegistrationsControllerService } from "../../../services/crossed-tables/determinant-registrations-controller.service";
import { DeterminantControllerService } from "../../../services/registration-controller/determinant-controller.service";
import { FormControllerService } from "../../../services/form-controller/form-controller.service";
import { ModalService } from "../../utils/base_modal.component";
import { TranslatedBaseError } from "../TranslatedBaseError";
import { TranslateService } from "@ngx-translate/core";
import { DeterminantType } from "../../../models/determinant-type";
import { FieldType } from "../../../models/field-type";
import { map } from 'rxjs/operators';
import * as _ from "lodash";

@Component({
  selector: "determinants-table",
  templateUrl: "./determinants-table.component.html",
  providers: [DeterminantRegistrationsControllerService, ModalService, ToastrService, FormControllerService],
  styles: [".cursor-hand {cursor: pointer;}"],
})
export class DeterminantsTableComponent extends TranslatedBaseError implements OnInit, OnChanges {
  @Input() public hideList: boolean = false;
  @Input() public reloadEvent: boolean;
  public serviceId: string;
  public hash: string;
  public determinantMapById: { [key: string]: string } = {};
  public baseUrl: string;
  public determinants: Determinant[] = [];
  public fields: any[] = [];
  public userFilter: any = { name: "" };
  public style: object = {};
  public formFields: any[] = [];

  public determinant: Determinant;
  public showEdit: boolean = false;
  public showAdd: boolean = false;
  public newCustomFilterBy: string;
  public elementsNumber: string;
  public allFields: any;

  constructor(private route: ActivatedRoute,
    toastr: ToastrService,
    public translateService: TranslateService,
    private formControllerService: FormControllerService,
    private determinantService: DeterminantControllerService) {
    super(toastr, translateService);
  }

  public ngOnInit() {
    this.route.params.subscribe((params) => {
      if (params.serviceId) {
        this.serviceId = params.serviceId;
        this.baseUrl = "/services/" + this.serviceId + "/determinantstable";
        this.hash = window.location.hash?.substring(1);
        this.getServiceDeterminantKeyName();
        this.getSystemDeterminants();
      }
    });
  }

  public getServiceDeterminantKeyName() {
    this.determinantService.getServiceDeterminantIdAndName(this.serviceId).subscribe(
      (data) => Object.assign(this.determinantMapById, data),
    );
  }

  public ngOnChanges(changes): void {
    if (this.serviceId) {
      this.getSystemDeterminants();
    }
  }

  public deleteDeterminant(item) {
    if (!Array.isArray(item)) {
      this.determinantService.deleteDeterminant(this.serviceId, item.id).subscribe(
        () => {
          this.responseSuccessful("Determinant deleted!");
          delete this.determinantMapById[item.id];
          this.getSystemDeterminants();
        },
        (error) => this.showErrorMessage(error),
      );
    } else {
      const promises = [];
      item.forEach((c) => {
        promises.push(
          this.determinantService.deleteDeterminant(this.serviceId, c.id).pipe(map(
            (data) => {
              return { id: c.id, data };
            },
          )).toPromise(),
        );
      });
      Promise.all<any[]>(promises).then((results: any[]) => {
        this.responseSuccessful("Determinants deleted!");
        results.forEach((r) => {
          delete this.determinantMapById[r.id];
        });
        this.getSystemDeterminants();
      });
    }
  }

  public saveDeterminant(item) {
    // Check for duplicates before saving (exclude the current item if it has an ID)
    // TO PUT BACK ONCE FIXED:
    /*
    if (this.isDuplicateDeterminant(item, item.id)) {
      this.showErrorMessage("A determinant with the same configuration already exists!");
      return;
    }
    */

    this.determinantService.updateDeterminant(item).subscribe(
      () => {
        this.responseSuccessful("Determinant saved!");
        this.determinantMapById[item.id] = item.name;
        this.getSystemDeterminants();
      },
      (error) => {
        item.isLoading = false;
        this.showErrorMessage(error);
      },
    );
  }

  public getSystemDeterminants() {
    const promises = [];
    promises.push(
      this.determinantService.getAllDeterminants(this.serviceId, true).pipe(map(
        (data) => {
          return data;
        },
      )).toPromise(),
    );
    promises.push(
      this.formControllerService.getFlattenFormFields(this.serviceId, false).pipe(map(
        (data) => {
          return data;
        },
      )).toPromise(),
    );
    Promise.all<any[]>(promises).then((results: any[]) => {
      this.determinants = results[0];
      this.allFields = results[1];
      this.elementsNumber = " (" + this.determinants.length + ")";
      for (let i = this.determinants.length; i--;) {
        this.determinants[i].status = this.getDeterminantStatus(this.determinants[i]);
        if (this.determinants[i].type === "all") {
          this.determinants.splice(i, 1);
        }
      }
      this.markDuplicateDeterminants();
      this.fieldNumberDeterminant(this.determinants, this.formFields);
    });
  }

  /**
   * Marks all determinants that have duplicates by adding an isDuplicate flag and collecting duplicate names.
   * This allows the UI to display a duplicate indicator with the names of matching determinants.
   */
  private markDuplicateDeterminants(): void {
    this.determinants.forEach((determinant) => {
      const duplicates = this.findDuplicateDeterminants(determinant, determinant.id);
      determinant.isDuplicate = duplicates.length > 0;
      determinant.duplicateNames = duplicates.map((d) => d.name);
    });
  }

  /**
   * Finds all determinants that are duplicates of the given determinant.
   * Returns an array of duplicate determinants (excluding the input determinant).
   */
  private findDuplicateDeterminants(determinant: Determinant, excludeId?: string): Determinant[] {
    return this.determinants.filter((existing) => {
      // Skip the same determinant if excludeId is provided
      if (excludeId && existing.id === excludeId) {
        return false;
      }

      // Skip if determinantType doesn't match
      if (existing.determinantType !== determinant.determinantType) {
        return false;
      }

      // Type-aware duplicate detection based on determinantType
      switch (determinant.determinantType) {
        case 'FORMFIELD':
          return this.isFormfieldDuplicate(existing, determinant);
        case 'BOT':
          return this.isBotDuplicate(existing, determinant);
        case 'EVENT':
          return this.isEventDuplicate(existing, determinant);
        case 'REGISTRATION':
          return this.isRegistrationDuplicate(existing, determinant);
        case 'COLLECTION_PLACE':
          return this.isCollectionPlaceDuplicate(existing, determinant);
        default:
          return false;
      }
    });
  }

  public checkDeterminantValue(det) {
    switch (det.determinantType) {
      case DeterminantType.BOT.toString():
        return true;
      case DeterminantType.COLLECTION_PLACE.toString():
        return true;
      case DeterminantType.SERVICE.toString():
        return true;
      case DeterminantType.FORMFIELD.toString():
        return true;
      // return det.type === "boolean" ? true : (String(det.value) && String(det.value).length > 0 ? true : false);
      default:
        return true;
    }
  }

  public checkFormfieldStatus(d): boolean {
    if (d) {
      let targetKey = d.targetFormFieldKey;
      if (d.type === FieldType.SELECTBOXES.toString()) {
        targetKey = targetKey.split(".")[0];
      }
      const hasTarget = this.allFields.find((f) => f.key === targetKey);
      const hasSource = this.allFields.find((f) => f.key === d.sourceFormFieldKey);
      return (d.classificationField || hasTarget) && (this.checkDeterminantValue(d) || hasSource) && !hasTarget?.fullKey.includes("_collection_");
    }
    return false;
  }

  public getDeterminantStatus(d): boolean {
    let status = true;
    switch (d.determinantType) {
      case DeterminantType.BOT.toString():
        status = (d.bots.length > 0);
        break;
      case DeterminantType.COLLECTION_PLACE.toString():
        status = true;
        break;
      case DeterminantType.SERVICE.toString():
        status = true;
        break;
      case DeterminantType.FORMFIELD.toString():
        // Check for event-based determinants (button, fileUpload, gridComponent)
        if ((d as any).buttonEventName !== undefined) {
          const det = d as any;
          status = (det.buttonKey && det.buttonKey.length > 0) && (det.buttonEventName && det.buttonEventName.length > 0);
        } else if ((d as any).fileUploadEventName !== undefined) {
          const det = d as any;
          status = (det.fileUploadKey && det.fileUploadKey.length > 0) && (det.fileUploadEventName && det.fileUploadEventName.length > 0);
        } else if ((d as any).gridEventName !== undefined) {
          const det = d as any;
          status = (det.gridKey && det.gridKey.length > 0) && (det.gridEventName && det.gridEventName.length > 0);
        } else if (d.eventName && d.eventComponent) {
          // Backwards compatibility: old-format event determinants used eventName and eventComponent
          // from base Determinant class (e.g., old "File is uploaded" determinants)
          status = true;
        } else {
          status = this.checkFormfieldStatus(d);
        }
        break;
      case DeterminantType.REGISTRATION.toString():
        status = (d.registrationId ? true : false);
        break;
      default:
        status = true;
    }
    return status;
  }

  public fieldNumberDeterminant(determinants: Determinant[], formFields: any[]) {
    determinants.forEach((determinant) => {
      determinant.fieldNumber = (determinant.usages ? determinant.usages.length : 0).toString();
    });
    determinants.sort((a, b) => (a.fieldNumber > b.fieldNumber) ? -1 : ((b.fieldNumber > a.fieldNumber) ? 1 : 0));
  }

  public onNewFilterBy(value) {
    this.newCustomFilterBy = value;
  }

  public loadDeterminant(determinant) {
    if (determinant) {
      this.determinant = this.determinants.find((it) => {
        return it.id === determinant.id;
      });
      this.showEdit = true;
    } else {
      this.determinant = new Determinant();
      this.determinant.name = this.newCustomFilterBy;
      this.determinant.type = "";
      this.showAdd = true;
    }
  }

  public onResizeEnd(event: ResizeEvent) {
    this.style = {
      width: `${event.rectangle.width}px`,
    };
  }

  public closeSlider() {
    this.getSystemDeterminants();
    this.closeLayers();
  }

  public closeLayers() {
    this.showAdd = false;
    this.showEdit = false;
    this.style = {};
    this.getSystemDeterminants();
  }

  /**
   * Checks if a determinant is duplicated based on its type and relevant properties.
   * Duplicate detection is type-aware to ensure semantic equivalence:
   *
   * - FORMFIELD: Compares targetFormFieldKey + operator + value properties
   * - BOT: Compares bots array
   * - EVENT: Compares eventName + eventComponent
   * - REGISTRATION: Compares registrationId
   * - COLLECTION_PLACE: Compares registrationServiceLocationId
   *
   * @param determinant - The determinant to check for duplicates
   * @param excludeId - Optional ID to exclude from comparison (useful when checking during edit)
   * @returns true if a duplicate is found, false otherwise
   */
  public isDuplicateDeterminant(determinant: Determinant, excludeId?: string): boolean {
    return this.determinants.some((existing) => {
      // Skip the same determinant if excludeId is provided
      if (excludeId && existing.id === excludeId) {
        return false;
      }

      // Skip if determinantType doesn't match
      if (existing.determinantType !== determinant.determinantType) {
        return false;
      }

      // Type-aware duplicate detection based on determinantType
      switch (determinant.determinantType) {
        case 'FORMFIELD':
          return this.isFormfieldDuplicate(existing, determinant);
        case 'BOT':
          return this.isBotDuplicate(existing, determinant);
        case 'EVENT':
          return this.isEventDuplicate(existing, determinant);
        case 'REGISTRATION':
          return this.isRegistrationDuplicate(existing, determinant);
        case 'COLLECTION_PLACE':
          return this.isCollectionPlaceDuplicate(existing, determinant);
        default:
          return false;
      }
    });
  }

  /**
   * Checks if two FORMFIELD determinants are duplicates.
   * Compares: targetFormFieldKey, operator, and all value properties relevant to the field type.
   */
  private isFormfieldDuplicate(existing: Determinant, determinant: Determinant): boolean {
    // Must target the same field
    if (existing.targetFormFieldKey !== determinant.targetFormFieldKey) {
      return false;
    }

    // Must have the same operator
    if (existing.operator !== determinant.operator) {
      return false;
    }

    // Compare all value-related properties for this field type
    const valueProperties = [
      'value',
      'sourceFormFieldKey',
      'classificationField',
      'gdbCatalogSelectedValue',
      'gdbCatalogFieldId',
      'gdbCatalogValue',
      'operatorStringValue',
      'textValue',
      'numericValue',
      'moneyValue',
      'booleanValue',
      'dateValue',
      'isCurrentDate',
      'selectValue',
      'classificationFields',
      'classificationFieldIds',
      'classificationGroup',
      'parentCatalogField',
      'subCatalogField',
      'buttonEventName',
      'buttonKey',
      'fileUploadEventName',
      'fileUploadKey',
      'gridEventName',
      'gridKey',
      'operator',
      'textOperator',
      'selectOperator',
      'classificationOperator',
      'determinantInsideGrid',
    ];

    const valueMatch = valueProperties.every((property) => {
      return _.isEqual(existing[property], determinant[property]);
    });

    if (!valueMatch) {
      return false;
    }

    // Check subject only if not inside grid
    if (!(existing as any).determinantInsideGrid) {
      if (!_.isEqual(existing.subject, determinant.subject)) {
        return false;
      }
    }

    // Check rowDeterminant specifically if it exists
    const existingRowDet = (existing as any).rowDeterminant;
    const determinantRowDet = (determinant as any).rowDeterminant;

    if (existingRowDet || determinantRowDet) {
      if (!existingRowDet || !determinantRowDet) {
        return false;
      }

      const rowDeterminantProperties = [
        'operator', 'textOperator', 'selectOperator', 'classificationOperator',
        'numericValue', 'moneyValue', 'textValue', 'booleanValue', 'dateValue', 'isCurrentDate', 'selectValue',
        'classificationField', 'classificationFields', 'classificationFieldIds', 'classificationGroup',
        'parentCatalogField', 'subCatalogField',
        'sourceFormFieldKey', 'targetFormFieldKey'
      ];

      return rowDeterminantProperties.every(prop => {
        return _.isEqual(existingRowDet[prop], determinantRowDet[prop]);
      });
    }

    return true;
  }

  /**
   * Checks if two BOT determinants are duplicates.
   * Compares: bots array
   */
  private isBotDuplicate(existing: Determinant, determinant: Determinant): boolean {
    const existingBots = existing.bots || [];
    const determinantBots = determinant.bots || [];

    // Compare bots arrays (must have same length and same IDs)
    if (existingBots.length !== determinantBots.length) {
      return false;
    }

    const existingBotIds = existingBots.map((b) => b.id || b).sort();
    const determinantBotIds = determinantBots.map((b) => b.id || b).sort();

    return existingBotIds.every((id, index) => id === determinantBotIds[index]);
  }

  /**
   * Checks if two EVENT determinants are duplicates.
   * Compares: eventName and eventComponent
   */
  private isEventDuplicate(existing: Determinant, determinant: Determinant): boolean {
    return (
      existing.eventName === determinant.eventName &&
      existing.eventComponent === determinant.eventComponent
    );
  }

  /**
   * Checks if two REGISTRATION determinants are duplicates.
   * Compares: registrationId
   */
  private isRegistrationDuplicate(existing: Determinant, determinant: Determinant): boolean {
    return existing.registrationId === determinant.registrationId;
  }

  /**
   * Checks if two COLLECTION_PLACE determinants are duplicates.
   * Compares: registrationLocationKey (acts as the location identifier)
   */
  private isCollectionPlaceDuplicate(existing: Determinant, determinant: Determinant): boolean {
    return existing.registrationLocationKey === determinant.registrationLocationKey;
  }

}
